import { useMemo } from "react";
import { useStore } from "ra-core";

import type { DealStage, LabeledValue, NoteStatus } from "../types";
import { defaultConfiguration } from "./defaultConfiguration";

export const CONFIGURATION_STORE_KEY = "app.configuration";

export interface ConfigurationContextValue {
  companySectors: LabeledValue[];
  currency: string;
  dealCategories: LabeledValue[];
  dealPipelineStatuses: string[];
  dealStages: DealStage[];
  /** Reasons offered when a deal moves to "lost"; "other" asks for a comment */
  dealLostReasons: LabeledValue[];
  noteStatuses: NoteStatus[];
  taskTypes: LabeledValue[];
  title: string;
  darkModeLogo: string;
  lightModeLogo: string;
  /** Owner dashboard: what counts and what is shown (Settings → Dashboard) */
  dashboard?: DashboardSettings;
}

export type DashboardSettings = {
  /** Tag that marks leads from before the CRM; kept out of the numbers */
  oldTagId?: number | null;
  /** A call within this many minutes counts as fast */
  fastMinutes?: number;
  /** Warn about bookings ending within this many days */
  bookingDays?: number;
  defaultPeriod?: "week" | "month" | "all";
  showDaily?: boolean;
  showAttention?: boolean;
  showApartments?: boolean;
  showManagers?: boolean;
};

export const useConfigurationContext = () => {
  const [config] = useStore<ConfigurationContextValue>(
    CONFIGURATION_STORE_KEY,
    defaultConfiguration,
  );
  // Merge with defaults so that missing fields in stored config
  // fall back to default values (e.g. when new settings are added)
  return useMemo(() => ({ ...defaultConfiguration, ...config }), [config]);
};

export const useConfigurationUpdater = () => {
  const [, setConfig] = useStore<ConfigurationContextValue>(
    CONFIGURATION_STORE_KEY,
  );
  return setConfig;
};
